import React, { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import { RefreshCw } from "lucide-react";
import { Ring } from "@uiball/loaders";
import axios from "axios";
import { fetchNetwork, sendToken, getFee, verifyTFAEmail, getUserInfo, getStrigaWalletAddress, sendSubWalletToken, sendSubWalletSolToken, sendSubWalletTron } from "@/utils/services/user.service";
import { getNetworkName } from "@/utils/helpers";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllCurrencies1 } from "@/redux/actions/FetchCoinLists";
import { getTotalBalance } from "@/redux/actions/sidebarActions";
import { API_URL } from "@/utils/services/user.service";
import authHeader from "@/utils/services/auth-header";
import config from "@/config/walletConnect";
import { Card, FlexRow, GhostButton } from "./styledComponents";
import FilterTabs from "./FilterTabs";
import TreemapSection from "./TreemapSection";
import InfoTokenView from "./InfoTokenView";
import WalletTable from "./WalletTable";
import AddressView from "./AddressView";
import ActivityView from "./ActivityView";
import DepositSidebar from "./DepositSidebar";
import WithdrawSidebar from "./WithdrawSidebar";

function WalletBlock(props) {
  const {
    title,
    subtitle,
    wallet,
    tab,
    onChangeTab,
    items,
    allItems,
    categoryTotals,
    onWalletDetails,
    onRefresh,
    pendingBalance,
    onRefreshUpcoming,
    showAddressView,
    addresses,
    onBack,
    activity,
    movements,
    onRowClick,
    onBackFromActivity,
    isLoadingActivities,
  } = props;

  const [sidebarDeposit, setSidebarDeposit] = useState(null);
  const [sidebarWithdraw, setSidebarWithdraw] = useState(null);
  const [infoToken, setInfoToken] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const searchTimeoutRef = useRef(null);
  const infoTokenRef = useRef(null);

  const dispatch = useDispatch();

  const { coinList: virtualWalletCoins } = useSelector(state => ({
    coinList: state.coinList1?.data || [],
  }));

  const {
    REACT_APP_SMART_CHAIN,
    REACT_APP_POLYGON_MATIC,
    REACT_APP_BTC,
    REACT_APP_SOLANA,
    REACT_APP_ETHER,
    REACT_APP_TRON
  } = config;

  const [withdrawFrom, setWithdrawFrom] = useState("internal");
  const [withdrawTo, setWithdrawTo] = useState("external_wallet");
  const [withdrawAddress, setWithdrawAddress] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawError, setWithdrawError] = useState("");
  const [selectedWithdrawNetwork, setSelectedWithdrawNetwork] = useState("");
  const [selectedWithdrawChainId, setSelectedWithdrawChainId] = useState("");
  const [withdrawFee, setWithdrawFee] = useState(null);
  const [isLoadingFee, setIsLoadingFee] = useState(false);
  const [withdrawOtp, setWithdrawOtp] = useState("");
  const [withdrawTfa, setWithdrawTfa] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [isOtpEnabled, setIsOtpEnabled] = useState(false);
  const [isTfaEnabled, setIsTfaEnabled] = useState(false);
  const [otpMinutes, setOtpMinutes] = useState(0);
  const [otpSeconds, setOtpSeconds] = useState(0);
  const [resendOtp, setResendOtp] = useState(false);
  const [otpCount, setOtpCount] = useState(0);
  
  const [withdrawCoin, setWithdrawCoin] = useState("");
  const [approverData, setApproverData] = useState([]);
  const [searchLoad, setSearchLoad] = useState(false);
  const [onChainWalletAddress, setOnChainWalletAddress] = useState(null);
  const [recieverSolAddress, setRecieverSolAddress] = useState("");
  const [recieverTrcAddress, setRecieverTrcAddress] = useState("");
  const [strigaWalletAddress, setStrigaWalletAddress] = useState(null);
  const [internalAmount, setInternalAmount] = useState(0);
  const [internalPrice, setInternalPrice] = useState(0);
  const [approxAmountAfterFee, setApproxAmountAfterFee] = useState(0);

  const [depositAddress, setDepositAddress] = useState("");
  const [depositNetworkData, setDepositNetworkData] = useState(null);
  const [depositNetworkOptions, setDepositNetworkOptions] = useState([]);
  const [selectedDepositNetwork, setSelectedDepositNetwork] = useState(null);
  const [isLoadingDepositNetwork, setIsLoadingDepositNetwork] = useState(false);

  const mapTabToNewFormat = (oldTab) => {
    if (oldTab === "coins") return "coin";
    if (oldTab === "tokens") return "token";
    if (oldTab === "stablecoins") return "stable-token";
    return oldTab;
  };

  const currentTab = mapTabToNewFormat(tab);
  const handleTabChange = (newTab) => {
    onChangeTab(newTab);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedItems = useMemo(() => {
    if (!sortColumn) return items;

    return [...items].sort((a, b) => {
      let aValue, bValue;

      if (sortColumn === "symbol") {
        aValue = (a.symbol || "").toUpperCase();
        bValue = (b.symbol || "").toUpperCase();
        if (sortDirection === "asc") {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      } else if (sortColumn === "qty") {
        aValue = parseFloat(a.qty || 0);
        bValue = parseFloat(b.qty || 0);
        if (sortDirection === "asc") {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      } else if (sortColumn === "balance") {
        aValue = parseFloat(a.balance || 0);
        bValue = parseFloat(b.balance || 0);
        if (sortDirection === "asc") {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      } else if (sortColumn === "price") {
        aValue = parseFloat(a.price || 0);
        bValue = parseFloat(b.price || 0);
        if (sortDirection === "asc") {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      } else if (sortColumn === "upcoming") {
        if (wallet === "Virtual" && pendingBalance) {
          const aPending = pendingBalance.find((coin) => coin.symbol === a.symbol);
          const bPending = pendingBalance.find((coin) => coin.symbol === b.symbol);
          aValue = aPending && aPending.quantity ? parseFloat(aPending.quantity) : 0;
          bValue = bPending && bPending.quantity ? parseFloat(bPending.quantity) : 0;
        } else {
          aValue = 0;
          bValue = 0;
        }
        if (sortDirection === "asc") {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      }

      return 0;
    });
  }, [items, sortColumn, sortDirection, wallet, pendingBalance]);

  const filteredItems = sortedItems;

  const itemsForTreemap = allItems || items;
  const totalForTreemap = itemsForTreemap.reduce((sum, a) => sum + (a.balance <= 0 ? 0 : a.balance), 0) || 1;

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const res = await getUserInfo();
        if (res.status === 200) {
          setUserInfo(res.data.User);
          setIsOtpEnabled(res.data.User?.otp_status === true);
          setIsTfaEnabled(res.data.User?.google_auth_status === true);
        } else {
          const userData = JSON.parse(localStorage.getItem("user"));
          if (userData?.data?.user) {
            setIsOtpEnabled(userData.data.user.otp_status === true);
            setIsTfaEnabled(userData.data.user.google_auth_status === true);
          }
        }
      } catch (error) {
        console.error("Error fetching user info:", error);
        const userData = JSON.parse(localStorage.getItem("user"));
        if (userData?.data?.user) {
          setIsOtpEnabled(userData.data.user.otp_status === true);
          setIsTfaEnabled(userData.data.user.google_auth_status === true);
        }
      }
    };

    fetchUserInfo();
  }, []);

  useEffect(() => {
    const fetchDepositNetworkData = async () => {
      if (!sidebarDeposit) {
        setDepositNetworkData(null);
        setDepositNetworkOptions([]);
        setSelectedDepositNetwork(null);
        setDepositAddress("");
        return;
      }

      setIsLoadingDepositNetwork(true);
      try {
        const walletType = wallet === "Virtual" ? "virtual" : "onchain";
        const payload = {
          symbol: sidebarDeposit.symbol,
          wallet_type: walletType,
        };
        const res = await fetchNetwork(payload);
        const networkData = res?.data?.data;

        if (networkData) {
          setDepositNetworkData(networkData);
          
          if (Array.isArray(networkData)) {
            const options = networkData.map((net) => {
              const chainId = net.network_name || net.chain_id;
              return {
                value: chainId,
                label: getNetworkName(chainId),
                address: net.address,
                chain_id: chainId,
              };
            });
            setDepositNetworkOptions(options);
            if (options.length > 0) {
              setSelectedDepositNetwork(options[0].value);
              setDepositAddress(options[0].address || "");
            }
          } else {
            const chainId = networkData.network_name || networkData.chain_id;
            const networkName = getNetworkName(chainId);
            setDepositNetworkOptions([
              {
                value: chainId,
                label: networkName,
                address: networkData.address,
                chain_id: chainId,
              },
            ]);
            setSelectedDepositNetwork(chainId);
            setDepositAddress(networkData.address || "");
          }
        }
      } catch (error) {
        console.error("Error fetching deposit network:", error);
        toast.error("Failed to fetch network information");
      } finally {
        setIsLoadingDepositNetwork(false);
      }
    };

    fetchDepositNetworkData();
  }, [sidebarDeposit, wallet]);

  useEffect(() => {
    if (selectedDepositNetwork && depositNetworkOptions.length > 0) {
      const selected = depositNetworkOptions.find((opt) => opt.value === selectedDepositNetwork);
      if (selected) {
        setDepositAddress(selected.address || "");
      }
    }
  }, [selectedDepositNetwork, depositNetworkOptions]);

  const performRecipientSearch = async (name) => {
    if (name.length < 3) {
      setApproverData([]);
      setSearchLoad(false);
      return;
    }

    setSearchLoad(true);
    try {
      const res = await axios.get(`${API_URL}users?name=${encodeURIComponent(name)}`, { headers: authHeader() });
      if (!res.data?.user) {
        setApproverData([]);
        setSearchLoad(false);
        return;
      }
      const arr = res.data.user.map((i) => ({
        id: i._id,
        address: i.address,
        value: i.name,
        label: i.name,
        sol_address: i?.sol_address,
        btc_address: i?.btcAddress,
        onChainAddress: i?.secondary_wallet_address,
        onChainSolAddress: i?.secondary_wallet_sol_address,
        trcWalletAddress: i?.trc_address,
        striga_user_id: i?.striga_user_id,
      }));

      const matchingOptions = arr.filter(
        (option) => option.value.toLowerCase() === name.toLowerCase()
      );
      
      if (matchingOptions.length > 0) {
        const selected = matchingOptions[0];
        setOnChainWalletAddress(selected);
        setRecieverSolAddress(selected.sol_address || "");
        setRecieverTrcAddress(selected.trcWalletAddress || "");
        
        if (withdrawTo === "onChain_wallet") {
          if (selectedWithdrawNetwork === REACT_APP_SOLANA) {
            setWithdrawAddress(selected.onChainSolAddress || "");
          } else {
            setWithdrawAddress(selected.onChainAddress || "");
          }
        } else if (withdrawTo === "internal_custodiy") {
          if (selectedWithdrawNetwork === REACT_APP_SMART_CHAIN || selectedWithdrawNetwork === REACT_APP_POLYGON_MATIC || selectedWithdrawNetwork === REACT_APP_ETHER) {
            setWithdrawAddress(selected.address || "");
          } else if (selectedWithdrawNetwork === REACT_APP_SOLANA) {
            setWithdrawAddress(selected.sol_address || "");
          } else if (selectedWithdrawNetwork === REACT_APP_TRON) {
            setWithdrawAddress(selected.trcWalletAddress || "");
          } else if (selectedWithdrawNetwork === REACT_APP_BTC) {
            setWithdrawAddress(selected.btc_address || "");
          }
        } else {
          setWithdrawAddress(selected.address || "");
        }
      }
      
      setApproverData(matchingOptions);
    } catch (error) {
      console.error("Error searching recipient:", error);
      setApproverData([]);
    } finally {
      setSearchLoad(false);
    }
  };

  const handleRecipientSearch = (name) => {
    if (name.length < 3) {
      setApproverData([]);
      setSearchLoad(false);
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
      return;
    }
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      performRecipientSearch(name);
      searchTimeoutRef.current = null;
    }, 2000);
  };

  useEffect(() => {
    const fetchStrigaWallet = async () => {
      if (withdrawTo === "bank" && onChainWalletAddress?.striga_user_id) {
        try {
          const res = await getStrigaWalletAddress({ striga_user_id: onChainWalletAddress.striga_user_id });
          setStrigaWalletAddress(res?.data?.IBAN || res?.data?.blockchainDepositAddress);
          if (res?.data?.IBAN) {
            setWithdrawAddress(res.data.IBAN);
          }
        } catch (error) {
          console.error("Error fetching Striga wallet:", error);
        }
      }
    };
    fetchStrigaWallet();
  }, [withdrawTo, onChainWalletAddress]);

  useEffect(() => {
    if (!onChainWalletAddress) return;
    
    if (withdrawTo === "onChain_wallet") {
      if (selectedWithdrawNetwork === REACT_APP_SOLANA) {
        setWithdrawAddress(onChainWalletAddress.onChainSolAddress || "");
      } else {
        setWithdrawAddress(onChainWalletAddress.onChainAddress || "");
      }
    } else if (withdrawTo === "internal_custodiy") {
      if (selectedWithdrawNetwork === REACT_APP_SMART_CHAIN || selectedWithdrawNetwork === REACT_APP_POLYGON_MATIC || selectedWithdrawNetwork === REACT_APP_ETHER) {
        setWithdrawAddress(onChainWalletAddress.address || "");
      } else if (selectedWithdrawNetwork === REACT_APP_SOLANA) {
        setWithdrawAddress(onChainWalletAddress.sol_address || "");
      } else if (selectedWithdrawNetwork === REACT_APP_TRON) {
        setWithdrawAddress(onChainWalletAddress.trcWalletAddress || "");
      } else if (selectedWithdrawNetwork === REACT_APP_BTC) {
        setWithdrawAddress(onChainWalletAddress.btc_address || "");
      } else {
        setWithdrawAddress(onChainWalletAddress.address || "");
      }
    } else if (withdrawTo === "bank" && strigaWalletAddress) {
      setWithdrawAddress(strigaWalletAddress);
    }
  }, [selectedWithdrawNetwork, withdrawTo, onChainWalletAddress, strigaWalletAddress]);

  useEffect(() => {
    if (sidebarWithdraw) {
      const coinSymbol = sidebarWithdraw.symbol;
      const qty = parseFloat(sidebarWithdraw.qty) || 0;
      const balance = parseFloat(sidebarWithdraw.balance) || 0;
      
      setWithdrawCoin(coinSymbol);
      setInternalAmount(qty);
      setInternalPrice(sidebarWithdraw.price || 0);
      
      const coinData = virtualWalletCoins?.find(c => c.symbol === coinSymbol);
      
      if (coinData?.chain_id) {
        const chainId = coinData.chain_id;
        setSelectedWithdrawNetwork(chainId);
        setSelectedWithdrawChainId(chainId);
        if (coinData.price || coinData.coin_price) {
          setInternalPrice(coinData.price || coinData.coin_price);
        }
      }
      
      setWithdrawAmount("");
      setWithdrawAddress("");
      setWithdrawFrom(wallet === "On-chain" ? "onchain" : "internal");
      setWithdrawTo(wallet === "On-chain" ? "internal" : "external_wallet");
      setWithdrawOtp("");
      setWithdrawTfa("");
      setOtpSent(false);
      setOtpMinutes(0);
      setOtpSeconds(0);
      setResendOtp(false);
      setOtpCount(0);
      setWithdrawError("");
      setWithdrawFee(null);
      setOnChainWalletAddress(null);
      setApproverData([]);
      setRecieverSolAddress("");
      setRecieverTrcAddress("");
      setStrigaWalletAddress(null);
      setApproxAmountAfterFee(0);
    } else {
      setWithdrawFrom(wallet === "On-chain" ? "onchain" : "internal");
      setWithdrawTo(wallet === "On-chain" ? "internal" : "external_wallet");
      setWithdrawAddress("");
      setWithdrawAmount("");
      setWithdrawCoin("");
      setSelectedWithdrawNetwork("");
      setSelectedWithdrawChainId("");
      setWithdrawOtp("");
      setWithdrawTfa("");
      setOtpSent(false);
      setOtpMinutes(0);
      setOtpSeconds(0);
      setResendOtp(false);
      setOtpCount(0);
      setWithdrawError("");
      setWithdrawFee(null);
      setOnChainWalletAddress(null);
      setApproverData([]);
      setRecieverSolAddress("");
      setRecieverTrcAddress("");
      setStrigaWalletAddress(null);
      setInternalAmount(0);
      setInternalPrice(0);
      setApproxAmountAfterFee(0);
    }
  }, [sidebarWithdraw, virtualWalletCoins, wallet]);

  useEffect(() => {
    if (withdrawAmount && withdrawFee) {
      const amount = parseFloat(withdrawAmount) || 0;
      const fee = withdrawFee?.percentage 
        ? (amount / 100 * withdrawFee.percentage)
        : parseFloat(withdrawFee?.fee || 0);
      setApproxAmountAfterFee(amount - fee);
    } else {
      setApproxAmountAfterFee(0);
    }
  }, [withdrawAmount, withdrawFee]);

  useEffect(() => {
    const fetchWithdrawFee = async () => {
      if (!sidebarWithdraw || !withdrawAmount || !withdrawCoin || Number(withdrawAmount) <= 0) {
        setWithdrawFee(null);
        return;
      }

      setIsLoadingFee(true);
      try {
        const payload = {
          symbol: withdrawCoin,
          amount: withdrawAmount,
        };
        const res = await getFee(payload);
        if (res?.data?.data) {
          setWithdrawFee(res.data.data);
        }
      } catch (error) {
        console.error("Error fetching fee:", error);
        setWithdrawFee(null);
      } finally {
        setIsLoadingFee(false);
      }
    };

    fetchWithdrawFee();
  }, [withdrawAmount, withdrawCoin, sidebarWithdraw]);

  function validateWithdraw(addr, amt, coin) {
    if (!amt || isNaN(Number(amt))) return "Amount not valid";
    if (Number(amt) <= 0) return "Amount must be > 0";
    if (!coin) return "Coin is required";
    if (coin && Number(amt) > internalAmount) return "Amount exceeds balance";
    if (!withdrawOtp && otpSent) return "OTP is required";
    if (isTfaEnabled && !withdrawTfa) return "2FA Code is required";
    return "";
  }

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // OTP Timer effect
  useEffect(() => {
    const interval = setInterval(() => {
      if (otpSeconds > 0) {
        setOtpSeconds(otpSeconds - 1);
      }

      if (otpSeconds === 0) {
        if (otpMinutes === 0) {
          clearInterval(interval);
        } else {
          setOtpSeconds(59);
          setOtpMinutes(otpMinutes - 1);
        }
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [otpSeconds, otpMinutes]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (infoToken && infoTokenRef.current && !infoTokenRef.current.contains(event.target)) {
        const target = event.target;
        
        const clickedButton = target.closest('button');
        if (clickedButton) {
          const row = clickedButton.closest('tr');
          const actionsCell = row?.querySelector('td:last-child');
          if (actionsCell && actionsCell.contains(clickedButton)) {
            return;
          }
        }
        
        setInfoToken(null);
      }
    };

    if (infoToken) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [infoToken]);

  useEffect(() => {
    if (sidebarWithdraw && withdrawCoin) {
      setWithdrawError(validateWithdraw(withdrawAddress, withdrawAmount, withdrawCoin));
    } else if (sidebarWithdraw && !withdrawCoin) {
      setWithdrawError("");
    }
  }, [withdrawAddress, withdrawAmount, withdrawCoin, sidebarWithdraw, selectedWithdrawNetwork, withdrawOtp, withdrawTfa, otpSent, isOtpEnabled, isTfaEnabled, withdrawTo, internalAmount]);

  const handleSendOtp = async () => {
    setIsSendingOtp(true);
    try {
      const res = await axios.post(`${API_URL}direct-transaction-otp`, "", { headers: authHeader() });
      if (res.status === 200) {
        setOtpSent(true);
        setOtpMinutes(1);
        setOtpSeconds(59);
        setResendOtp(false);
        toast.success(res.data.message || "OTP sent to your email");
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      const message = error?.response?.data?.message || error.message || "Failed to send OTP";
      toast.error(message);
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    setIsSendingOtp(true);
    try {
      const res = await axios.post(`${API_URL}direct-transaction-otp`, "", { headers: authHeader() });
      if (res.status === 200) {
        setOtpSent(true);
        setOtpMinutes(1);
        setOtpSeconds(59);
        setResendOtp(false);
        setOtpCount(otpCount + 1);
        toast.success(res.data.message || "OTP resent to your email");
      }
    } catch (error) {
      console.error("Error resending OTP:", error);
      const message = error?.response?.data?.message || error.message || "Failed to resend OTP";
      toast.error(message);
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleMaxAmount = () => {
    if (internalAmount > 0) {
      setWithdrawAmount(String(internalAmount));
    }
  };

  const handleWithdrawSubmit = async () => {
    if (withdrawError) {
      toast.error(withdrawError);
      return;
    }

    // Always send OTP first if not entered (security step - user will always receive OTP on email)
    if (!withdrawOtp) {
      if (!otpSent) {
        // Auto-send OTP on first click
        await handleSendOtp();
        return; // Don't proceed with transaction, wait for user to enter OTP
      } else {
        // OTP was sent but not entered yet
        toast.error("Please enter the OTP code");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      if (isTfaEnabled && withdrawTfa) {
        const userData = JSON.parse(localStorage.getItem("user"));
        const tfaRequestData = {
          token: withdrawTfa,
          email: userData?.data?.user?.email,
        };
        try {
          const tfaRes = await verifyTFAEmail(tfaRequestData);
          if (!tfaRes.data?.verified) {
            toast.error("Invalid 2FA Code");
            setIsSubmitting(false);
            return;
          }
        } catch (tfaError) {
          toast.error(tfaError?.response?.data?.Message || "2FA verification failed");
          setIsSubmitting(false);
          return;
        }
      }

      let modifiedCoin = withdrawCoin;
      if (modifiedCoin === "USDT (ERC20)") {
        modifiedCoin = "USDT_ERC20";
      } else if (modifiedCoin === "USDT (BEP20)") {
        modifiedCoin = "USDT";
      }

      let modifiedReceiver = withdrawAddress;
      if (withdrawTo === "onChain_wallet") {
        if (selectedWithdrawNetwork === REACT_APP_SOLANA) {
          modifiedReceiver = onChainWalletAddress?.onChainSolAddress || withdrawAddress;
        } else {
          modifiedReceiver = onChainWalletAddress?.onChainAddress || withdrawAddress;
        }
      } else if (withdrawTo === "internal_custodiy") {
        if (selectedWithdrawNetwork === REACT_APP_SMART_CHAIN || selectedWithdrawNetwork === REACT_APP_POLYGON_MATIC || selectedWithdrawNetwork === REACT_APP_ETHER) {
          modifiedReceiver = onChainWalletAddress?.address || withdrawAddress;
        } else if (selectedWithdrawNetwork === REACT_APP_SOLANA) {
          modifiedReceiver = onChainWalletAddress?.sol_address || withdrawAddress;
        } else if (selectedWithdrawNetwork === REACT_APP_TRON) {
          modifiedReceiver = recieverTrcAddress || withdrawAddress;
        } else {
          modifiedReceiver = withdrawAddress;
        }
      } else if (withdrawTo === "bank") {
        modifiedReceiver = strigaWalletAddress || withdrawAddress;
      }

      let sendTokenFunction;
      let sendTokenPayload;

      if (wallet === "Virtual") {
        if ((selectedWithdrawNetwork === REACT_APP_SOLANA || selectedWithdrawNetwork === 123) && withdrawTo === "onChain_wallet") {
          sendTokenFunction = sendSubWalletSolToken;
          sendTokenPayload = {
            coin: modifiedCoin,
            amount: withdrawAmount,
            otp: withdrawOtp,
            wallet_address: modifiedReceiver || withdrawAddress
          };
        } else if (selectedWithdrawNetwork === REACT_APP_TRON) {
          sendTokenFunction = sendToken;
          sendTokenPayload = {
            coin: modifiedCoin,
            amount: withdrawAmount,
            otp: withdrawOtp,
            wallet_address: recieverTrcAddress || modifiedReceiver || withdrawAddress
          };
        } else if (withdrawTo === "onChain_wallet") {
          sendTokenFunction = sendSubWalletToken;
          sendTokenPayload = {
            coin: modifiedCoin,
            amount: withdrawAmount,
            otp: withdrawOtp,
            wallet_address: modifiedReceiver || withdrawAddress
          };
        } else {
          sendTokenFunction = sendToken;
          sendTokenPayload = {
            amount: withdrawAmount,
            receiver: modifiedReceiver,
            coin: modifiedCoin,
            otp: withdrawOtp,
          };
        }
      } else if (wallet === "On-chain") {
        if (selectedWithdrawNetwork === REACT_APP_SOLANA) {
          sendTokenFunction = sendSubWalletSolToken;
          sendTokenPayload = {
            coin: modifiedCoin,
            amount: withdrawAmount,
            otp: withdrawOtp,
            wallet_address: modifiedReceiver || withdrawAddress || onChainWalletAddress?.onChainSolAddress
          };
        } else if (selectedWithdrawNetwork === REACT_APP_TRON) {
          sendTokenFunction = sendSubWalletTron;
          sendTokenPayload = {
            coin: modifiedCoin,
            amount: withdrawAmount,
            otp: withdrawOtp,
            wallet_address: recieverTrcAddress || modifiedReceiver || withdrawAddress
          };
        } else {
          sendTokenFunction = sendSubWalletToken;
          sendTokenPayload = {
            coin: modifiedCoin,
            amount: withdrawAmount,
            otp: withdrawOtp,
            wallet_address: modifiedReceiver || withdrawAddress || onChainWalletAddress?.onChainAddress
          };
        }
      } else {
        sendTokenFunction = sendToken;
        sendTokenPayload = {
          amount: withdrawAmount,
          receiver: modifiedReceiver,
          coin: modifiedCoin,
          otp: withdrawOtp,
        };
      }

      const res = await sendTokenFunction(sendTokenPayload);

      if (res.status === 200) {
        toast.success(res?.data?.receipt || "Withdrawal successful");
        dispatch(getTotalBalance());
        dispatch(fetchAllCurrencies1());
        setSidebarWithdraw(null);
        setWithdrawAmount("");
        setWithdrawAddress("");
        setWithdrawCoin("");
        setWithdrawOtp("");
        setWithdrawTfa("");
      setOtpSent(false);
      setOtpMinutes(0);
      setOtpSeconds(0);
      setResendOtp(false);
      setOtpCount(0);
      setWithdrawError("");
      setWithdrawFrom("internal");
      setWithdrawTo(wallet === "On-chain" ? "internal" : "external_wallet");
      setSelectedWithdrawNetwork("");
      setOnChainWalletAddress(null);
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.Message ||
        error?.response?.data?.Error ||
        error.message ||
        "Withdrawal failed";
      toast.error(message);
      if (error?.response?.data?.Message === "Invalid Otp") {
        setWithdrawOtp("");
        setResendOtp(true);
        setOtpCount(otpCount + 1);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTypeLabel = (type) => {
    if (type === "coin") return "Coin";
    if (type === "token") return "Token";
    if (type === "stable-token") return "Stablecoin";
    return type;
  };

  return (
    <>
      {isRefreshing && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
            zIndex: 9999,
          }}
        >
          <Ring size={65} color="#5ce9ff" />
        </div>
      )}
      <WithdrawSidebar
        sidebarWithdraw={sidebarWithdraw}
        wallet={wallet}
        withdrawFrom={withdrawFrom}
        withdrawTo={withdrawTo}
        withdrawAddress={withdrawAddress}
        withdrawAmount={withdrawAmount}
        withdrawCoin={withdrawCoin}
        withdrawError={withdrawError}
        withdrawFee={withdrawFee}
        withdrawOtp={withdrawOtp}
        withdrawTfa={withdrawTfa}
        selectedWithdrawNetwork={selectedWithdrawNetwork}
        internalAmount={internalAmount}
        approxAmountAfterFee={approxAmountAfterFee}
        isLoadingFee={isLoadingFee}
        isOtpEnabled={isOtpEnabled}
        isTfaEnabled={isTfaEnabled}
        otpSent={otpSent}
        isSendingOtp={isSendingOtp}
        isSubmitting={isSubmitting}
        searchLoad={searchLoad}
        approverData={approverData}
        onClose={() => setSidebarWithdraw(null)}
        onFromChange={(value) => {
          setWithdrawFrom(value);
          setWithdrawAmount("");
        }}
        onToChange={(value) => {
          setWithdrawTo(value);
          setWithdrawAddress("");
          setOnChainWalletAddress(null);
          setApproverData([]);
        }}
        onAddressChange={setWithdrawAddress}
        onAmountChange={setWithdrawAmount}
        onOtpChange={setWithdrawOtp}
        onTfaChange={setWithdrawTfa}
        onRecipientSelect={(option) => {
          setOnChainWalletAddress(option);
          setRecieverSolAddress(option.sol_address || "");
          setRecieverTrcAddress(option.trcWalletAddress || "");
          setApproverData([]);
        }}
        onRecipientSearch={handleRecipientSearch}
        onMaxAmount={handleMaxAmount}
        onSendOtp={handleSendOtp}
        onResendOtp={handleResendOtp}
        onSubmit={handleWithdrawSubmit}
        otpMinutes={otpMinutes}
        otpSeconds={otpSeconds}
        resendOtp={resendOtp}
        otpCount={otpCount}
        REACT_APP_SOLANA={REACT_APP_SOLANA}
        REACT_APP_SMART_CHAIN={REACT_APP_SMART_CHAIN}
        REACT_APP_POLYGON_MATIC={REACT_APP_POLYGON_MATIC}
        REACT_APP_ETHER={REACT_APP_ETHER}
        REACT_APP_TRON={REACT_APP_TRON}
        REACT_APP_BTC={REACT_APP_BTC}
      />

      <DepositSidebar
        sidebarDeposit={sidebarDeposit}
        onClose={() => setSidebarDeposit(null)}
        selectedDepositNetwork={selectedDepositNetwork}
        depositNetworkOptions={depositNetworkOptions}
        isLoadingDepositNetwork={isLoadingDepositNetwork}
        depositAddress={depositAddress}
        onNetworkChange={setSelectedDepositNetwork}
      />

      <Card>
        <FlexRow gap={8} style={{ justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h3 style={{ marginBottom: 4 }}>{title}</h3>
            <p style={{ color: "#94a3b8", fontSize: 12, marginTop: 0 }}>{subtitle}</p>
          </div>
          <FlexRow gap={6}>
            {onRefresh && (
              <GhostButton 
                onClick={async () => {
                  setIsRefreshing(true);
                  await new Promise(resolve => setTimeout(resolve, 0));
                  try {
                    const result = onRefresh();
                    if (result && typeof result.then === 'function') {
                      await result;
                    } else {
                      await new Promise(resolve => setTimeout(resolve, 500));
                    }
                  } finally {
                    setIsRefreshing(false);
                  }
                }}
                disabled={isRefreshing}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px',
                  padding: '8px 14px',
                  opacity: isRefreshing ? 0.6 : 1,
                  cursor: isRefreshing ? 'not-allowed' : 'pointer',
                }}
              >
                Refresh Balance
              </GhostButton>
            )}
            <GhostButton onClick={onWalletDetails}>Wallet details</GhostButton>
          </FlexRow>
        </FlexRow>
        
        {categoryTotals && (
          <div style={{ marginTop: "8px", marginBottom: "8px", display: "flex", justifyContent: "flex-end" }}>
            {(() => {
              const coinsValue = parseFloat(categoryTotals.coins || 0);
              const tokensValue = parseFloat(categoryTotals.tokens || 0);
              const stablecoinsValue = parseFloat(categoryTotals.stableTokens || 0);
              
              let dominantKey = null;
              const maxValue = Math.max(coinsValue, tokensValue, stablecoinsValue);
              
              if (maxValue > 0) {
                if (maxValue === coinsValue && coinsValue >= tokensValue && coinsValue >= stablecoinsValue) {
                  dominantKey = "coin";
                } else if (maxValue === tokensValue && tokensValue >= stablecoinsValue) {
                  dominantKey = "token";
                } else if (maxValue === stablecoinsValue) {
                  dominantKey = "stable-token";
                }
              }
              
              return currentTab ? (
                <div
                  style={{
                    padding: "4px 12px",
                    borderRadius: "999px",
                    border: "1px solid #22c55e",
                    background: "rgba(34, 197, 94, 0.15)",
                    color: "#e5e7eb",
                    fontSize: "12px",
                    fontWeight: 500,
                  }}
                >
                  Dominant: {currentTab}
                </div>
              ) : null;
            })()}
          </div>
        )}
        <FilterTabs currentTab={currentTab} onTabChange={handleTabChange} categoryTotals={categoryTotals} />


        <WalletTable
          items={filteredItems}
          wallet={wallet}
          pendingBalance={pendingBalance}
          activity={activity}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={handleSort}
          onRowClick={onRowClick}
          onDepositClick={(a) => {
            setSidebarDeposit(a);
            setDepositAddress("");
          }}
          onWithdrawClick={(a) => setSidebarWithdraw(a)}
          onInfoClick={(a) => setInfoToken(a)}
        />

{infoToken ? (
          <div ref={infoTokenRef}>
            <InfoTokenView
              infoToken={infoToken}
              onClose={() => setInfoToken(null)}
              getTypeLabel={getTypeLabel}
            />
          </div>
        ) : showAddressView ? (
          <AddressView
            wallet={wallet}
            addresses={addresses}
            onBack={onBack}
          />
        ) : activity && activity.wallet === wallet ? (
          <ActivityView
            wallet={wallet}
            activity={activity}
            movements={movements}
            isLoadingActivities={isLoadingActivities}
            onBack={onBackFromActivity}
          />
        ) : (
          <TreemapSection items={itemsForTreemap} wallet={wallet} />
        )}
      </Card>
    </>
  );
}

export default WalletBlock;
